import type { ReactElement } from 'react';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { Frame } from '../momentum-v3/primitives';
import { StudioNode } from './nodes/index';
import type { StudioNodeContext } from './nodes/box';
import { StudioDebugPanel, isStudioDebugRequested } from './StudioDebugPanel';

/**
 * Build Engine Selector v1, Stream C: the Studio renderer. Mounts the same
 * `Frame` (momentum_v3 chrome — rounded card + three-zone footer, page
 * number, agency/client lockup; repointed from momentum_v2 when momentum_v1/
 * v2 were sunset from the web app) every other momentum_v3 archetype mounts,
 * then absolutely positions every `ResolvedSceneNode` from
 * `section.studio.resolved` by its own `box` — already absolute canvas px
 * in the shared 1920x1080 space (`ResolvedSceneNode.box`'s doc comment in
 * `src/build-engines/studio/types.ts`) — sorted by `z`. Nothing here ever
 * re-invokes the design agent; export/presentation just replays this same
 * geometry (spec §60).
 *
 * `surface` (dark/light card) isn't yet in the persisted contract — no
 * per-slide field carries it (`DeckDesignDirection.pacing` only has
 * `dominantMode`/`expectedDensity`, not a card surface). Until Stream B
 * adds one, this infers a reasonable default from the slide's own design
 * intent (hero slides dark, everything else light — the same split
 * `CoverPage`/`StrategyPage` vs. `AudienceProfilesPage`/`GeoStrategyPage`
 * already draw in the hand-authored momentum_v2 archetypes). Stream B:
 * if/when `StudioSectionState` grows an explicit surface, replace this.
 */
function inferSurface(props: PageProps): 'dark' | 'light' {
  const intent = props.section.studio?.designSpec.intent;
  return intent === 'hero' ? 'dark' : 'light';
}

export function StudioPage(props: PageProps): ReactElement | null {
  const { proposal, section, actions, readOnly } = props;
  const resolved = section.studio?.resolved;
  if (!resolved) return null;

  const ctx: StudioNodeContext = { proposal, section, actions, readOnly };
  const surface = inferSurface(props);

  const isDev = Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);
  if (isDev && resolved.warnings.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(`[studio] ${resolved.slideId}:`, resolved.warnings);
  }

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv2-studio-root"
      hideTitle
    >
      <Frame surface={surface} proposal={proposal} section={section}>
        {null}
      </Frame>
      <div className="tpl-studio-canvas" data-slide-id={resolved.slideId}>
        <StudioNode node={resolved.root} ctx={ctx} />
      </div>
      {!readOnly && isStudioDebugRequested() ? <StudioDebugPanel proposal={proposal} section={section} /> : null}
    </PageShell>
  );
}
